import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiFileText, FiX, FiSearch, FiInfo, FiBarChart2, FiPieChart, FiList } from "react-icons/fi";
import Chart from "react-apexcharts";

import { BASE_URL } from "../../../../../BaseUrl";
import { getCookie } from "../../../../../functions/Functions";
import "./FinanceDashboard.css";

const BUCKET_COLORS = {
  "Current": "#3b82f6",
  "1–30 days": "#f59e0b",
  "1-30 days": "#f59e0b",
  "31–60 days": "#f97316",
  "31-60 days": "#f97316",
  "60+ days": "#ef4444",
  "over_60_days": "#ef4444",
};

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "Rs 0";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  if (num === 0) return "Rs 0";
  if (num >= 1000000) {
    return `Rs ${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `Rs ${(num / 1000).toFixed(1)}k`;
  }
  return `Rs ${num.toLocaleString()}`;
};

const PayableAgeingWidget = ({ startDate, endDate, branch }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewType, setViewType] = useState("bar"); // "bar" | "donut" | "list"

  const fetchData = async () => {
    setIsLoading(true);
    const fromDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : moment().subtract(30, "days").format("YYYY-MM-DD");
    const toDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    const payload = { fromDate, toDate };
    if (branch) {
      if (Array.isArray(branch) && branch.length > 0) {
        payload.branch_id = branch.map((b) => b.id);
      } else if (branch?.id) {
        payload.branch_id = branch.id;
      }
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/widgets/accounting-payable-ageing`,
        payload,
        {
          headers: { Authorization: `Bearer ${getCookie()}` },
        }
      );
      if (response?.data?.data) {
        setData(response.data.data);
      } else if (response?.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching accounting payable ageing:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, branch]);

  // Extract chart data
  const chartItems = data?.chart_data || [
    { label: "Current", amount: data?.summary?.current || 0 },
    { label: "1–30 days", amount: data?.summary?.["1_30_days"] || 0 },
    { label: "31–60 days", amount: data?.summary?.["31_60_days"] || 0 },
    { label: "60+ days", amount: data?.summary?.over_60_days || 0 },
  ];

  const maxAmount = Math.max(...chartItems.map((item) => Number(item.amount) || 0), 1);

  // Generate 5 tick marks for x-axis
  const ticks = [0, 0.25, 0.5, 0.75, 1.0].map((ratio) =>
    formatCurrency(maxAmount * ratio)
  );

  // Invoices handling for Modal
  const rawInvoices = data?.invoices || [];

  const filteredInvoices = rawInvoices.filter((inv) => {
    const matchesBucket =
      selectedBucket === "All" ||
      (inv.bucket || "").toLowerCase().includes(selectedBucket.toLowerCase());
    const matchesSearch =
      !searchTerm ||
      (inv.pi_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.supplier_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchesBucket && matchesSearch;
  });

  const totalAmountSum = filteredInvoices.reduce((acc, inv) => acc + (Number(inv.total_amount) || 0), 0);
  const paidAmountSum = filteredInvoices.reduce((acc, inv) => acc + (Number(inv.paid_amount) || 0), 0);
  const unpaidAmountSum = filteredInvoices.reduce((acc, inv) => acc + (Number(inv.unpaid_amount) || 0), 0);

  const getBucketBadgeClass = (bucket = "") => {
    const b = bucket.toLowerCase();
    if (b.includes("current")) return "bucket-badge bucket-current";
    if (b.includes("1") || b.includes("30")) return "bucket-badge bucket-1-30";
    if (b.includes("31") || b.includes("60")) return "bucket-badge bucket-31-60";
    return "bucket-badge bucket-60-plus";
  };

  const getTabBtnClass = (tab) => {
    const isSelected = selectedBucket === tab;
    if (!isSelected) return "bucket-filter-btn";
    if (tab === "All") return "bucket-filter-btn active active-all";
    if (tab.includes("Current")) return "bucket-filter-btn active active-current";
    if (tab.includes("1") || tab.includes("30")) return "bucket-filter-btn active active-1-30";
    if (tab.includes("31") || tab.includes("60")) return "bucket-filter-btn active active-31-60";
    return "bucket-filter-btn active active-60-plus";
  };

  const totalAgeingAmount = chartItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const overdue30Plus = chartItems
    .filter((item) => (item.label || "").includes("31") || (item.label || "").includes("60"))
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const critical60Plus = chartItems
    .filter((item) => (item.label || "").includes("60") || (item.label || "").includes("over_60"))
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  // ApexCharts Configurations
  const seriesBar = [
    {
      name: "Payable Amount",
      data: chartItems.map((item) => Number(item.amount) || 0),
    },
  ];

  const optionsBar = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "inherit",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: "56%",
        distributed: true,
      },
    },
    colors: chartItems.map((item) => BUCKET_COLORS[item.label] || "#3b82f6"),
    labels: chartItems.map((item) => item.label),
    xaxis: {
      categories: chartItems.map((item) => item.label),
      labels: {
        style: { colors: "#6b7280", fontSize: "11px", fontWeight: 500 },
        formatter: (val) => formatCurrency(val),
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#374151", fontSize: "12px", fontWeight: 600 },
      },
    },
    grid: {
      strokeDashArray: 4,
      borderColor: "#e5e7eb",
    },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: { colors: ["#1f2937"], fontSize: "11px", fontWeight: 600 },
      formatter: (val) => formatCurrency(val),
      offsetX: 8,
    },
    states: {
      hover: {
        filter: { type: "darken", value: 0.92 },
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const item = chartItems[dataPointIndex] || {};
        const label = item.label || "Bucket";
        const amount = Number(item.amount) || 0;
        const color = BUCKET_COLORS[label] || "#3b82f6";
        const pct = totalAgeingAmount > 0 ? ((amount / totalAgeingAmount) * 100).toFixed(1) : "0.0";
        return `
          <div style="background:#ffffff; padding:10px 14px; border-radius:8px; box-shadow:0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1); border:1px solid #e5e7eb; font-size:12px; font-family:inherit;">
            <div style="display:flex; align-items:center; gap:6px; font-weight:700; color:#1f2937; margin-bottom:6px;">
              <span style="width:10px; height:10px; border-radius:50%; background-color:${color}; display:inline-block;"></span>
              ${label} Dues
            </div>
            <div style="color:#4b5563; margin-bottom:2px;">Outstanding: <strong style="color:#111827; font-weight:700;">Rs ${amount.toLocaleString()}</strong></div>
            <div style="color:#6b7280; font-size:11px;">Share: <strong style="color:#2563eb;">${pct}%</strong> of total payables</div>
          </div>
        `;
      },
    },
  };

  const seriesDonut = chartItems.map((item) => Number(item.amount) || 0);

  const optionsDonut = {
    chart: {
      type: "donut",
      fontFamily: "inherit",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
      },
    },
    labels: chartItems.map((item) => item.label),
    colors: chartItems.map((item) => BUCKET_COLORS[item.label] || "#3b82f6"),
    legend: {
      position: "right",
      fontSize: "12px",
      fontWeight: 500,
      markers: { width: 10, height: 10, radius: 5 },
      itemMargin: { vertical: 4 },
      formatter: (seriesName, opts) => {
        const val = opts.w.globals.series[opts.seriesIndex];
        return `${seriesName}: ${formatCurrency(val)}`;
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: { enabled: false },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const label = w.globals.labels[seriesIndex] || "Bucket";
        const amount = series[seriesIndex] || 0;
        const color = BUCKET_COLORS[label] || "#3b82f6";
        const pct = totalAgeingAmount > 0 ? ((amount / totalAgeingAmount) * 100).toFixed(1) : "0.0";
        return `
          <div style="background:#ffffff; padding:10px 14px; border-radius:8px; box-shadow:0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1); border:1px solid #e5e7eb; font-size:12px; font-family:inherit;">
            <div style="display:flex; align-items:center; gap:6px; font-weight:700; color:#1f2937; margin-bottom:6px;">
              <span style="width:10px; height:10px; border-radius:50%; background-color:${color}; display:inline-block;"></span>
              ${label} Dues
            </div>
            <div style="color:#4b5563; margin-bottom:2px;">Outstanding: <strong style="color:#111827; font-weight:700;">Rs ${Number(amount).toLocaleString()}</strong></div>
            <div style="color:#6b7280; font-size:11px;">Share: <strong style="color:#2563eb;">${pct}%</strong> of total payables</div>
          </div>
        `;
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Dues",
              fontSize: "12px",
              color: "#6b7280",
              formatter: () => formatCurrency(totalAgeingAmount),
            },
          },
        },
      },
    },
  };

  return (
    <>
      <div className="payable-ageing-card">
        {/* Header */}
        <div className="payable-ageing-header mb-2">
          <div>
            <div className="d-flex align-items-center gap-1">
              <h5 className="payable-ageing-title m-0 mr-1">Accounts Payable Ageing</h5>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="payable-ageing-info-tooltip">
                    Categorizes unpaid supplier invoices into time buckets based on invoice age to monitor cash outflow timing and overdue liabilities.
                  </Tooltip>
                }
              >
                <span className="payable-info-icon-btn ms-1" style={{ cursor: "pointer" }}>
                  <FiInfo className="text-muted" style={{ fontSize: "14px" }} />
                </span>
              </OverlayTrigger>
            </div>
            <span className="payable-ageing-subtitle">
              Supplier dues {data?.as_of_date ? `(As of ${data.as_of_date})` : ""}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="btn-group btn-group-sm me-1" role="group">
              <OverlayTrigger placement="top" overlay={<Tooltip id="bar-chart-tt">Bar Chart View</Tooltip>}>
                <button
                  type="button"
                  className={`btn btn-sm view-toggle-btn ${viewType === "bar" ? "active" : ""}`}
                  onClick={() => setViewType("bar")}
                >
                  <FiBarChart2 />
                </button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip id="donut-chart-tt">Donut Chart View</Tooltip>}>
                <button
                  type="button"
                  className={`btn btn-sm view-toggle-btn ${viewType === "donut" ? "active" : ""}`}
                  onClick={() => setViewType("donut")}
                >
                  <FiPieChart />
                </button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip id="list-view-tt">Table Breakdown View</Tooltip>}>
                <button
                  type="button"
                  className={`btn btn-sm view-toggle-btn ${viewType === "list" ? "active" : ""}`}
                  onClick={() => setViewType("list")}
                >
                  <FiList size={12} />
                </button>
              </OverlayTrigger>
            </div>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="view-invoices-tooltip">View itemized invoice breakdown & supplier details</Tooltip>}
            >
              <button
                type="button"
                className="payable-ageing-icon-btn"
                onClick={() => setShowModal(true)}
              >
                <FiFileText />
              </button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Summary Metric Pills */}
        {!isLoading && (
          <div
            className="d-flex align-items-center justify-content-between gap-2 py-1 px-2 mb-2 bg-light rounded border"
            style={{ fontSize: "12px" }}
          >
            <div className="d-flex align-items-center gap-1">
              <span className="text-muted">Total Dues:</span>
              <span className="fw-bold text-dark">{formatCurrency(totalAgeingAmount)}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <span className="text-muted">Overdue (&gt;30d):</span>
              <span className={`fw-bold ${overdue30Plus > 0 ? "text-warning" : "text-success"}`}>
                {formatCurrency(overdue30Plus)}
              </span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <span className="text-muted">Critical (&gt;60d):</span>
              <span className={`fw-bold ${critical60Plus > 0 ? "text-danger" : "text-success"}`}>
                {formatCurrency(critical60Plus)}
              </span>
            </div>
          </div>
        )}

        {/* Chart Content */}
        {isLoading ? (
          <div className="payable-ageing-chart-container py-1">
            {[80, 60, 40, 25].map((widthPct, idx) => (
              <div key={idx} className="payable-chart-row mb-3">
                <div
                  className="finance-skeleton-box me-3"
                  style={{ width: "100px", height: "14px", flexShrink: 0 }}
                />
                <div className="payable-chart-bar-wrap">
                  <div
                    className="finance-skeleton-box"
                    style={{ width: `${widthPct}%`, height: "36px", borderRadius: "6px" }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="payable-ageing-chart-container">
            {viewType === "bar" ? (
              <div style={{ width: "100%", height: "230px" }}>
                <Chart options={optionsBar} series={seriesBar} type="bar" height="230" width="100%" />
              </div>
            ) : viewType === "donut" ? (
              <div style={{ width: "100%", height: "230px" }}>
                <Chart options={optionsDonut} series={seriesDonut} type="donut" height="230" width="100%" />
              </div>
            ) : (
              <div className="table-responsive" style={{ minHeight: "230px", maxHeight: "230px" }}>
                <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "11.5px" }}>
                  <thead className="table-light">
                    <tr>
                      <th>Age Bucket</th>
                      <th className="text-end">Outstanding Amount</th>
                      <th className="text-end">Share %</th>
                      <th className="text-center">Category Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartItems.map((item, idx) => {
                      const label = item.label || `Bucket ${idx + 1}`;
                      const amount = Number(item.amount) || 0;
                      const pctOfTotal = totalAgeingAmount > 0 ? ((amount / totalAgeingAmount) * 100).toFixed(1) : "0.0";
                      const color = BUCKET_COLORS[label] || "#3b82f6";
                      return (
                        <tr key={label}>
                          <td className="py-2">
                            <span
                              className="d-inline-block rounded-circle mr-2"
                              style={{ width: 8, height: 8, backgroundColor: color }}
                            />
                            <span className="fw-semibold text-dark">{label}</span>
                          </td>
                          <td className="text-end fw-bold py-2 text-dark">Rs {amount.toLocaleString()}</td>
                          <td className="text-end text-muted py-2">{pctOfTotal}%</td>
                          <td className="text-center py-2">
                            <span className={getBucketBadgeClass(label)}>
                              {label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="table-light fw-bold">
                    <tr>
                      <td>Total Payables</td>
                      <td className="text-end text-primary">Rs {totalAgeingAmount.toLocaleString()}</td>
                      <td className="text-end">100%</td>
                      <td className="text-center text-muted">{chartItems.length} Buckets</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invoices List Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className="invoice-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6 font-weight-bold mb-0">
            Accounts Payable Invoices
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {/* Controls: Filter & Search */}
          <div className="row g-2 align-items-center mb-3">
            <div className="col-12 col-md-6">
              <div className="btn-group btn-group-sm w-100 bucket-filter-group" role="group">
                {["All", "Current", "1–30 days", "31–60 days", "60+ days"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={getTabBtnClass(tab)}
                    onClick={() => setSelectedBucket(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by PI # or Supplier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Invoices Table */}
          <div className="table-responsive invoice-modal-table-wrap" style={{ minHeight: "360px", maxHeight: "360px" }}>
            <table className="table table-hover table-striped align-middle invoice-modal-table mb-0">
              <thead>
                <tr>
                  <th>PI Number</th>
                  <th>Supplier</th>
                  <th>Doc Date</th>
                  <th>Age (Days)</th>
                  <th>Bucket</th>
                  <th className="text-end">Total Amount</th>
                  <th className="text-end">Paid</th>
                  <th className="text-end">Unpaid</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((inv, index) => (
                    <tr key={inv.pi_number || index}>
                      <td className="fw-semibold">{inv.pi_number || "N/A"}</td>
                      <td>{inv.supplier_name || `Supplier #${inv.supplier_id}`}</td>
                      <td>{inv.document_date || "-"}</td>
                      <td>{inv.age_days ?? 0} days</td>
                      <td>
                        <span className={getBucketBadgeClass(inv.bucket)}>
                          {inv.bucket || "Current"}
                        </span>
                      </td>
                      <td className="text-end">
                        Rs {(Number(inv.total_amount) || 0).toLocaleString()}
                      </td>
                      <td className="text-end text-success">
                        Rs {(Number(inv.paid_amount) || 0).toLocaleString()}
                      </td>
                      <td className="text-end text-danger fw-bold">
                        Rs {(Number(inv.unpaid_amount) || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      No invoices found for this criteria.
                    </td>
                  </tr>
                )}
              </tbody>
              {filteredInvoices.length > 0 && (
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan="5" className="text-end">
                      Total:
                    </td>
                    <td className="text-end">Rs {totalAmountSum.toLocaleString()}</td>
                    <td className="text-end text-success">
                      Rs {paidAmountSum.toLocaleString()}
                    </td>
                    <td className="text-end text-danger">
                      Rs {unpaidAmountSum.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <button
            type="button"
            className="invoice-modal-close-btn"
            onClick={() => setShowModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PayableAgeingWidget;
